import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { getUser, getUserTrialState, formatTurkishName } from '@/utils/storage';
import { auth, db } from '@/firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { openPaddleCheckout, getPriceIdForPlan, PADDLE_PRICE_ID, IS_PAYMENT_ACTIVE } from '@/services/paddle';
import { changePlan } from '@/services/subscriptionService';
import { checkIsPro, getPreviewRole } from '@/utils/planAccess';
import { updateCanonicalUrl } from '@/utils/seo';
import { toast } from 'sonner';


const TRANSLATIONS = {
  tr: {
    nav1: 'Panelim',
    nav2: 'Çalışma / Sözlük',
    nav3: 'Morfemler',
    nav4: 'Oyunlar',
    nav6: 'Tarifeler',
    trial: 'Deneme',
    trialUpper: '3 GÜNLÜK DENEME',
    daysLeft: 'gün kaldı',
    currentPlan: 'MEVCUT PLAN',
    title: 'Sana uygun tarifeyi seç',
    sub: 'Abonelik planlarında 3 gün ₺0 deneme süresi. Dilediğin an tek tıkla iptal et.',
    titleVisitor: 'Terimleri parçala, anla, birleştir.',
    subVisitor: 'Abonelik planlarında 3 gün ₺0 deneme. Dilediğin an tek tıkla iptal et, 3 gün sonra yıllık faturalandırılır.',
    noCardNote: 'Bugün ₺0 çekilir · Dilediğin an iptal et.',
    startTrial: '3 gün ücretsiz dene',
    save: '2 ay bedava',
    tabNames: ['Temel', 'Pro', 'Ömür Boyu'],
    monthly: 'Aylık',
    yearly: 'Yıllık',
    compare: 'ÖZELLİKLER',
    pBasic: 'TEMEL',
    pPro: 'PRO',
    pLife: 'ÖMÜR BOYU',
    cancel: 'Tek tıkla iptal, soru sorulmaz',
    vat: 'Fiyatlara KDV dahil',
    secure: '256-bit güvenli ödeme',
    perMo: '/ ay',
    perYr: '/ yıl',
    perOnce: 'tek ödeme',
    free: '₺0',
    plans: [
      {
        name: 'Temel',
        tag: '16 kategori sözlüğü, 100 morfem, sınırsız Flashcard & Eşleştirme.',
        mo: '₺99',
        yrp: '₺790',
        noteMo: 'Aylık faturalandırılır',
        noteYr: '3 gün ücretsiz · Sonra ₺790/yıl (₺66/ay)',
        cta: '3 Gün Ücretsiz Dene',
        feats: [
          ['✓', '16 anatomik kategori · 100 morfem'],
          ['✓', 'Sınırsız Flashcard ve Eşleştirme oyunları'],
          ['✓', 'Kişisel ilerleme takibi'],
          ['–', 'Quiz ve Morfem Yapıcı (Pro ve üzeri)'],
          ['–', 'Tüm 571+ morfem havuzu (Pro ve üzeri)']
        ]
      },
      {
        name: 'Pro',
        tag: 'Tam öğrenme deneyimi; 4 mod, seviye, tekrar.',
        badge: 'SINIRLI SÜRE · %55',
        mo: null,
        yrp: '₺2.000',
        old: '₺4.500',
        noteYr: '3 gün ücretsiz · Sonra ₺2.000/yıl (₺167/ay)',
        noteMo: 'Sadece yıllık · ₺167/ay',
        cta: '3 Gün Ücretsiz Dene',
        feats: [
          ['✓', '16 kategori · 571+ morfem'],
          ['✓', '4 oyun modu'],
          ['✓', 'İlerleme ve seviye sistemi'],
          ['✓', 'Öğrendiğin terimlerle tekrar'],
          ['✓', 'Gelecek modüller dahil']
        ]
      },
      {
        name: 'Ömür Boyu',
        tag: 'Bir kez öde süresiz kullan.',
        badge: 'SINIRLI SÜRE · %25',
        old: '₺8.000',
        once: '₺5.990',
        note: 'Tek ödeme · Deneme süresi yoktur (Doğrudan satın alma)',
        cta: 'Ömür Boyu Satın Al',
        feats: [
          ['✓', '16 kategori · 571+ morfem'],
          ['✓', '4 oyun modu'],
          ['✓', 'İlerleme ve seviye sistemi'],
          ['✓', 'Öğrendiğin terimlerle tekrar'],
          ['✓', 'Gelecek modüller dahil']
        ]
      }
    ],
    rows: [
      ['Çalışma / Sözlük terimleri', '16 Kategori (Tümü açık)', '16 Kategori (Tümü açık)', '16 Kategori (Tümü açık)'],
      ['Morfem kütüphanesi', '100 morfem', '571+ (Tümü)', '571+ (Tümü)'],
      ['Flashcard · Eşleştirme', 'Sınırsız', 'Sınırsız', 'Sınırsız'],
      ['Quiz · Morfem Yapıcı', '– (Kilitli)', '✓', '✓'],
      ['İlerleme takibi', 'Flashcard & Eşleştirme', 'Tüm oyunlar + Seviye sistemi', 'Tüm oyunlar + Seviye sistemi'],
      ['Öğrenilen terimlerle tekrar', '–', '✓', '✓'],
      ['Gelecek modüller dahil', '–', '✓', '✓'],
      ['3 Gün Ücretsiz Deneme (₺0)', '✓', '✓', '– (Doğrudan Satın Alma)'],
      ['Faturalandırma', 'Yıllık', 'Yıllık', 'Tek Seferlik (Süresiz)']
    ]
  },
  en: {
    nav1: 'Dashboard',
    nav2: 'Study / Glossary',
    nav3: 'Morphemes',
    nav4: 'Games',
    nav6: 'Pricing',
    trial: 'Trial',
    trialUpper: '3-DAY TRIAL',
    daysLeft: 'days left',
    currentPlan: 'CURRENT PLAN',
    title: 'Pick the plan that fits',
    sub: '3-day $0 trial on subscription plans. Cancel anytime in one click.',
    titleVisitor: 'Split, understand, rebuild terms.',
    subVisitor: '3 days $0 trial on subscription plans. Cancel anytime in 1 click, billed annually after 3 days.',
    noCardNote: '$0 billed today · Cancel anytime in one click.',
    startTrial: 'Try 3 days free',
    save: '2 months free',
    tabNames: ['Basic', 'Pro', 'Lifetime'],
    monthly: 'Monthly',
    yearly: 'Yearly',
    compare: 'FEATURES',
    pBasic: 'BASIC',
    pPro: 'PRO',
    pLife: 'LIFETIME',
    cancel: 'Cancel in one click, no questions',
    vat: 'VAT included',
    secure: '256-bit secure payment',
    perMo: '/ mo',
    perYr: '/ yr',
    perOnce: 'one-time',
    free: '₺0',
    plans: [
      {
        name: 'Basic',
        tag: '16 categories glossary, 100 morphemes, unlimited Flashcards & Matching.',
        mo: '₺99',
        yrp: '₺790',
        noteMo: 'Billed monthly',
        noteYr: '3 days free · Then ₺790/yr (₺66/mo)',
        cta: 'Start 3-Day Free Trial',
        feats: [
          ['✓', '16 anatomical categories · 100 morphemes'],
          ['✓', 'Unlimited Flashcards and Matching games'],
          ['✓', 'Personal progress tracking'],
          ['–', 'Quiz and Morpheme Builder (Pro & above)'],
          ['–', 'Full 571+ morpheme library (Pro & above)']
        ]
      },
      {
        name: 'Pro',
        tag: 'The full experience; 4 modes, levels, review.',
        badge: 'LIMITED TIME · 55% OFF',
        mo: null,
        yrp: '₺2,000',
        old: '₺4,500',
        noteYr: '3 days free · Then ₺2,000/yr (₺167/mo)',
        noteMo: 'Yearly only · ₺167/mo',
        cta: 'Start 3-Day Free Trial',
        feats: [
          ['✓', '16 categories · 571+ morphemes'],
          ['✓', '4 game modes'],
          ['✓', 'Progress and level system'],
          ['✓', 'Review with learned terms'],
          ['✓', 'Future modules included']
        ]
      },
      {
        name: 'Lifetime',
        tag: 'Pay once, use forever.',
        badge: 'LIMITED TIME · 25% OFF',
        old: '₺8,000',
        once: '₺5,990',
        note: 'One payment · No trial period (Direct purchase)',
        cta: 'Buy Lifetime Plan',
        feats: [
          ['✓', '16 categories · 571+ morphemes'],
          ['✓', '4 game modes'],
          ['✓', 'Progress and level system'],
          ['✓', 'Review with learned terms'],
          ['✓', 'Future modules included']
        ]
      }
    ],
    rows: [
      ['Study / Glossary terms', 'All 16 Categories Unlocked', 'All 16 Categories Unlocked', 'All 16 Categories Unlocked'],
      ['Morpheme library', '100 morphemes', '571+ (All)', '571+ (All)'],
      ['Flashcard · Matching', 'Unlimited', 'Unlimited', 'Unlimited'],
      ['Quiz · Morpheme Builder', '– (Locked)', '✓', '✓'],
      ['Progress tracking', 'Flashcard & Matching', 'All games + Level system', 'All games + Level system'],
      ['Review with learned terms', '–', '✓', '✓'],
      ['Future modules included', '–', '✓', '✓'],
      ['3-Day Free Trial ($0)', '✓', '✓', '– (Direct Purchase)'],
      ['Billing', 'Yearly', 'Yearly', 'One-time (Lifetime)']
    ]
  }
};

export const PricingView = () => {
  const navigate = useNavigate();
  const { currentLanguage, setLanguage } = useLanguage();
  const [mobileTab, setMobileTab] = useState(1); // 0: Temel, 1: Pro, 2: Ömür
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const previewRole = getPreviewRole();
  const [userData, setUserData] = useState(null);
  const [isUserPro, setIsUserPro] = useState(previewRole === 'pro');

  const isTr = currentLanguage === 'tr';
  const yr = true;
  const t = TRANSLATIONS[isTr ? 'tr' : 'en'];

  const [currentUser, setCurrentUser] = useState(() => {
    if (previewRole) {
      return { uid: 'preview-uid', email: 'dr.kaya@healthlexmed.com', displayName: 'Dr. Ahmet Kaya' };
    }
    return auth?.currentUser || getUser();
  });

  useEffect(() => {
    updateCanonicalUrl('https://www.healthlexmed.com/pricing');
  }, []);

  useEffect(() => {
    if (previewRole) return;
    const unsub = onAuthStateChanged(auth, (usr) => {
      setCurrentUser(usr || getUser());
    });
    return () => unsub();
  }, [previewRole]);
  const userUid = currentUser?.uid;
  const trialState = getUserTrialState(currentUser);
  const trialDaysLeft = trialState.daysLeft;

  // Plan ve Pro Durumu Kontrolü (Firestore canlı dinleme + localStorage fallback)
  useEffect(() => {
    if (previewRole) {
      setUserData({ isPro: previewRole === 'pro', isBasic: previewRole === 'basic' });
      return;
    }
    const uid = userUid || auth?.currentUser?.uid;
    if (!uid) {
      const localUser = getUser();
      setUserData(localUser);
      setIsUserPro(checkIsPro(localUser));
      return;
    }

    try {
      const userDocRef = doc(db, 'users', uid);
      const unsub = onSnapshot(userDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData(data);
          setIsUserPro(checkIsPro(data));
        } else {
          const local = getUser();
          setUserData(local);
          setIsUserPro(checkIsPro(local));
        }
      }, (err) => {
        console.warn('[Pricing] Could not read user pro status:', err);
        const local = getUser();
        setUserData(local);
        setIsUserPro(checkIsPro(local));
      });
      return () => unsub();
    } catch (e) {
      console.warn('[Pricing] Firestore snapshot error:', e);
    }
  }, [previewRole, userUid]);

  // Kullanıcının mevcut planının tespiti (subscriptionStatus, plan, isLifetime, isBasic, isPro)
  const userCurrentPlan = useMemo(() => {
    const data = userData || (currentUser?.uid ? currentUser : null) || getUser() || {};
    
    // 1. Ömür Boyu VIP (tek seferlik ödeme, süresiz tam erişim)
    const isLifetime = data.isLifetime === true || 
      data.planType === 'lifetime' || 
      (data.plan || '').toLowerCase().includes('lifetime') || 
      (data.plan || '').toLowerCase().includes('ömür');
    if (isLifetime) return 'lifetime';

    const subStatus = (data.subscriptionStatus || '').toLowerCase();
    const isBasicFlag = data.isBasic === true || data.planType === 'basic' || (data.plan || '').toLowerCase().includes('basic') || (data.plan || '').toLowerCase().includes('temel');
    const isProFlag = data.isPro === true || data.planType === 'pro' || (data.plan || '').toLowerCase().includes('pro');

    // Aktif abonelik kontrolü
    const hasActiveSubscription = subStatus === 'active' || subStatus === 'trialing' || subStatus === 'past_due' || isBasicFlag || isProFlag;
    if (!hasActiveSubscription || subStatus === 'canceled' || subStatus === 'free') {
      return null;
    }

    if (isBasicFlag && !isProFlag) return 'basic';
    if (isProFlag) return 'pro';
    if ((data.plan || '').toLowerCase().includes('basic') || (data.plan || '').toLowerCase().includes('temel')) return 'basic';
    if ((data.plan || '').toLowerCase().includes('pro')) return 'pro';

    return null;
  }, [userData, currentUser]);

  const selPlan = t.plans[mobileTab] || t.plans[0];
  const isMobBasic = mobileTab === 0;
  const isMobPro = mobileTab === 1;
  const isMobLife = mobileTab === 2;

  let mobPrice = selPlan.yrp;
  let mobPer = t.perYr;
  let mobNote = selPlan.noteYr;

  if (isMobLife) {
    mobPrice = selPlan.once;
    mobPer = t.perOnce;
    mobNote = selPlan.note;
  } else if (isMobPro) {
    mobPrice = selPlan.yrp;
    mobPer = t.perYr;
    mobNote = selPlan.noteYr;
  } else if (isMobBasic) {
    mobPrice = selPlan.yrp;
    mobPer = t.perYr;
    mobNote = selPlan.noteYr;
  }

  const isCurrentMobilePlan =
    (mobileTab === 0 && userCurrentPlan === 'basic') ||
    (mobileTab === 1 && userCurrentPlan === 'pro') ||
    (mobileTab === 2 && userCurrentPlan === 'lifetime');

  const handlePlanClick = async (planIndex) => {
    const clickedPlanKey = planIndex === 0 ? 'basic' : planIndex === 1 ? 'pro' : 'lifetime';

    if (userCurrentPlan === 'lifetime') {
      toast.info(isTr ? 'Zaten Ömür Boyu VIP üyeliğiniz bulunmaktadır.' : 'You already have Lifetime VIP access.');
      return;
    }

    if (userCurrentPlan === clickedPlanKey) {
      // Zaten bu planda, tekrar satın almaya izin verme
      navigate('/dashboard');
      return;
    }

    // Auth Guard — Oturumu olmayan misafir kullanıcılar için
    if (!currentUser) {
      try {
        sessionStorage.setItem('healthlex_selected_plan', clickedPlanKey);
      } catch (e) {}
      const loginMsg = isTr
        ? 'İşleme devam etmek için lütfen önce ücretsiz hesap oluşturun veya giriş yapın.'
        : 'Please create a free account or sign in to continue.';
      toast.info(loginMsg, { duration: 4000 });
      navigate(`/register?plan=${clickedPlanKey}&redirect=${encodeURIComponent('/pricing')}`);
      return;
    }

    if (!IS_PAYMENT_ACTIVE) {
      toast.info(isTr ? 'Ödeme sistemi yakında aktif olacak.' : 'Payment system coming soon.');
      return;
    }

    // Kullanıcının mevcut aktif aboneliği varsa → merkezi changePlan servisi
    const hasActiveSub = !!(userData?.paddleSubscriptionId);
    if (hasActiveSub && userCurrentPlan && clickedPlanKey !== userCurrentPlan) {
      // Downgrade onayı
      const currentRank = { basic: 1, pro: 2, lifetime: 3 }[userCurrentPlan] || 0;
      const targetRank  = { basic: 1, pro: 2, lifetime: 3 }[clickedPlanKey]  || 0;

      if (targetRank < currentRank && clickedPlanKey !== 'lifetime') {
        const confirmed = window.confirm(
          isTr
            ? `${userCurrentPlan === 'pro' ? 'Pro' : 'mevcut'} planınızdan Temel plana düşürüleceksiniz. Mevcut dönem sonunda yeni fiyat uygulanacak, anlık iade yapılmayacak. Onaylıyor musunuz?`
            : `You will downgrade to the Basic plan. The new price takes effect at the end of your current billing period. No immediate refund. Proceed?`
        );
        if (!confirmed) return;
      }

      if (clickedPlanKey === 'lifetime' && userData?.paddleSubscriptionId) {
        const confirmed = window.confirm(
          isTr
            ? 'Mevcut yıllık aboneliğiniz ANINDA iptal edilecek ve Ömür Boyu satın alma ekranı açılacak. Kalan süreniz için iade yapılmayacak. Onaylıyor musunuz?'
            : 'Your current subscription will be cancelled immediately and the Lifetime checkout will open. No refund for unused time. Proceed?'
        );
        if (!confirmed) return;
      }

      setCheckoutLoading(true);
      try {
        // auth.currentUser — Pricing'de firebaseUser state yok, doğrudan auth.currentUser kullanılır
        const firebaseUser = auth.currentUser;
        const result = await changePlan({
          firebaseUser,
          firestoreData: userData,
          targetPlanKey: clickedPlanKey,
          currentPlanKey: userCurrentPlan,
          isTr
        });

        if (result.success && !result.requiresCheckout) {
          toast.success(
            isTr
              ? 'Planınız başarıyla değiştirildi 🎉'
              : 'Your plan has been updated successfully 🎉'
          );
        } else if (!result.success && result.error) {
          toast.error(result.error);
        }
        // requiresCheckout = true → openPaddleCheckout servis içinde zaten açıldı
      } catch (err) {
        console.error('[Pricing] Plan change error:', err);
        toast.error(isTr ? 'Beklenmeyen bir hata oluştu.' : 'An unexpected error occurred.');
      } finally {
        setCheckoutLoading(false);
      }
      return;
    }

    // Yeni kullanıcı (abonelik yok) veya ilk kez satın alma → doğrudan checkout
    const planTitle = clickedPlanKey === 'pro'
      ? 'Annual Pro Membership'
      : clickedPlanKey === 'basic'
      ? 'Basic Plan (Yearly)'
      : 'Lifetime Membership';

    setCheckoutLoading(true);
    try {
      await openPaddleCheckout({
        priceId: getPriceIdForPlan(clickedPlanKey),
        customerEmail: currentUser?.email || undefined,
        customData: {
          plan: planTitle,
          planId: clickedPlanKey,
          billingPeriod: clickedPlanKey === 'lifetime' ? 'lifetime' : 'yearly',
          userId: currentUser?.uid || 'unknown'
        }
      });
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setCheckoutLoading(false);
    }
  };


  // Kayıt sonrası seçili planla gelindiğinde otomatik checkout başlatma
  useEffect(() => {
    if (!currentUser || checkoutLoading) return;
    const params = new URLSearchParams(window.location.search);
    let planToCheckout = params.get('checkout');
    if (!planToCheckout) {
      try {
        planToCheckout = sessionStorage.getItem('healthlex_selected_plan');
      } catch (e) {}
    }

    if (planToCheckout && ['basic', 'pro', 'lifetime'].includes(planToCheckout)) {
      try {
        sessionStorage.removeItem('healthlex_selected_plan');
      } catch (e) {}

      if (params.has('checkout')) {
        params.delete('checkout');
        const remaining = params.toString();
        const newUrl = window.location.pathname + (remaining ? `?${remaining}` : '');
        window.history.replaceState({}, '', newUrl);
      }

      const planIndex = planToCheckout === 'basic' ? 0 : planToCheckout === 'lifetime' ? 2 : 1;
      setTimeout(() => {
        handlePlanClick(planIndex);
      }, 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  return (
    <div className="min-h-screen bg-background text-foreground antialiased transition-colors flex flex-col justify-between">
      {/* ==================================================================== */}
      {/* 1) MOBİL GÖRÜNÜM (lg:hidden): FABLE SEÇENEK TABLI & TEK KART TASARIMI */}
      {/* ==================================================================== */}
      <div className="w-full flex lg:hidden flex-col gap-4 max-w-md mx-auto px-4 py-6 pb-28">
        {/* Mobil Üst Başlık Kartı (Dark Banner) */}
        <div className="bg-[#0f1b33] dark:bg-slate-900 text-white rounded-2xl p-5 shadow-sm flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div
              onClick={() => navigate('/')}
              className="font-extrabold text-base tracking-tight font-sans cursor-pointer"
            >
              Health<span className="font-serif italic font-bold text-[#8fb3ff]">Lex</span>Med
            </div>
            <div className="flex items-center gap-2">
              {/* TR / EN Dil Seçici */}
              <div
                onClick={() => setLanguage(isTr ? 'en' : 'tr')}
                className="flex bg-white/10 rounded-lg p-0.5 text-[11px] font-extrabold cursor-pointer select-none"
                role="button"
                tabIndex={0}
              >
                <span className={`px-2 py-1 rounded-md transition-all ${isTr ? 'bg-primary text-white' : 'text-slate-300'}`}>
                  TR
                </span>
                <span className={`px-2 py-1 rounded-md transition-all ${!isTr ? 'bg-primary text-white' : 'text-slate-300'}`}>
                  EN
                </span>
              </div>
              {currentUser ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-xs font-bold text-white/90 hover:text-white underline cursor-pointer"
                >
                  {isTr ? 'Panelim' : 'Dashboard'}
                </button>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="text-xs font-bold text-white/90 hover:text-white underline cursor-pointer"
                >
                  {isTr ? 'Giriş yap' : 'Log in'}
                </button>
              )}
            </div>
          </div>

          <div>
            <h1 className="m-0 font-serif font-semibold text-2xl leading-snug text-white">
              {t.titleVisitor}
            </h1>
            <p className="m-0 text-xs sm:text-sm text-slate-300 leading-relaxed mt-2">
              {t.subVisitor}
            </p>
          </div>
        </div>

        {/* 4 Segmentli Plan Tab Seçici ([Ücretsiz] [Temel] [Pro] [Ömür]) */}
        <div className="flex bg-card border border-border rounded-xl p-1 font-bold text-xs shadow-xs select-none">
          {t.tabNames.map((name, idx) => {
            const isSelected = mobileTab === idx;
            return (
              <button
                key={idx}
                onClick={() => setMobileTab(idx)}
                className={`flex-1 py-2.5 rounded-lg text-center transition-all cursor-pointer ${
                  isSelected
                    ? 'bg-[#0f1b33] dark:bg-primary text-white shadow-xs font-extrabold'
                    : 'text-muted-foreground hover:text-foreground font-semibold'
                }`}
              >
                {name}
              </button>
            );
          })}
        </div>

        {/* Seçili Plan Kartı */}
        <div
          className={`bg-card border rounded-2xl p-5 sm:p-6 flex flex-col gap-4 relative shadow-sm transition-all ${
            isMobPro ? 'border-primary ring-2 ring-primary/20' : 'border-border'
          }`}
        >
          {/* Rozet */}
          {isCurrentMobilePlan ? (
            <span className="absolute -top-3 left-5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-extrabold text-[10px] tracking-wider py-1 px-3 rounded-md shadow-xs">
              {isTr ? 'MEVCUT PLANINIZ' : 'CURRENT PLAN'}
            </span>
          ) : (
            selPlan.badge && (
              <span
                className={`absolute -top-3 left-5 text-white font-extrabold text-[10px] tracking-wider py-1 px-3 rounded-md shadow-xs ${
                  isMobLife ? 'bg-gradient-to-r from-blue-600 to-indigo-600' : 'bg-orange-500'
                }`}
              >
                {selPlan.badge}
              </span>
            )
          )}

          <div>
            <div className="font-extrabold text-lg sm:text-xl text-foreground">{selPlan.name}</div>
            <div className="font-normal text-xs sm:text-sm leading-snug text-muted-foreground mt-1">
              {selPlan.tag}
            </div>
          </div>

          {/* Fiyat Alanı */}
          <div className="flex flex-col gap-1">
            {selPlan.old && (
              <span className="font-semibold text-xs text-muted-foreground/60 line-through">
                {selPlan.old}
              </span>
            )}
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-extrabold text-3xl sm:text-4xl text-foreground tracking-tight">
                {mobPrice}
              </span>
              {mobPer && <span className="font-semibold text-xs sm:text-sm text-muted-foreground">{mobPer}</span>}
            </div>
            <span className="text-xs text-muted-foreground">{mobNote}</span>
          </div>

          {/* Sadece Yıllık Seçeneği (Temel veya Pro için) */}
          {(isMobBasic || isMobPro) && (
            <div className="flex bg-muted/60 border border-border rounded-xl p-1 font-bold text-xs select-none">
              <span className="flex-1 py-2 text-center rounded-lg bg-card text-foreground shadow-xs flex items-center justify-center gap-1.5 font-bold">
                {t.yearly} · {t.save}
              </span>
            </div>
          )}

          {/* Özellik Listesi */}
          <div className="flex flex-col gap-2.5 border-t border-border/80 pt-4">
            {selPlan.feats.map((f, fIdx) => {
              const isChecked = f[0] === '✓';
              return (
                <div
                  key={fIdx}
                  className={`flex gap-2.5 items-start text-xs sm:text-sm leading-snug font-medium ${
                    isChecked ? 'text-foreground' : 'text-muted-foreground/60'
                  }`}
                >
                  <span
                    className={`font-extrabold flex-none ${
                      isChecked ? 'text-primary' : 'text-muted-foreground/40'
                    }`}
                  >
                    {f[0]}
                  </span>
                  <span>{f[1]}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Güven Rozeti */}
        <div className="text-center font-semibold text-xs text-muted-foreground py-1">
          ✓ {t.cancel} · ✓ {t.vat} · 🔒 {t.secure}
        </div>

        {/* Mobil Alt Yapışkan CTA Alanı */}
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-md border-t border-border p-3 sm:p-4 shadow-lg lg:hidden flex flex-col gap-2">
          {(() => {
            const isMobLifetimeLocked = userCurrentPlan === 'lifetime' && mobileTab < 2;

            return (
              <button
                onClick={() => handlePlanClick(mobileTab)}
                disabled={checkoutLoading || isCurrentMobilePlan || isMobLifetimeLocked}
                className={`w-full text-center font-bold text-sm sm:text-base py-3 px-4 rounded-xl transition-all shadow-sm ${
                  isCurrentMobilePlan
                    ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 cursor-not-allowed opacity-90'
                    : isMobLifetimeLocked
                    ? 'bg-muted text-muted-foreground border border-border cursor-not-allowed opacity-75'
                    : isMobPro
                    ? 'bg-gradient-to-r from-[#2b7fff] to-[#5aa9ff] hover:from-[#2563eb] hover:to-[#3b82f6] text-white shadow-md cursor-pointer'
                    : 'bg-primary text-primary-foreground cursor-pointer'
                }`}
              >
                {isCurrentMobilePlan
                  ? (isTr ? 'Mevcut Planınız' : 'Current Plan')
                  : isMobLifetimeLocked
                  ? (isTr ? 'Zaten Ömür Boyu Erişiminiz Var' : 'Already Have Lifetime Access')
                  : `${selPlan.cta} →`}
              </button>
            );
          })()}
          <div className="text-xs text-muted-foreground text-center">
            {t.noCardNote}{' '}
            <button
              onClick={() => handlePlanClick(1)}
              className="font-bold text-primary hover:underline cursor-pointer"
            >
              {t.startTrial}
            </button>
          </div>
        </div>
      </div>

      {/* ==================================================================== */}
      {/* 2) MASAÜSTÜ GÖRÜNÜM (hidden lg:flex): 3'LÜ GENİŞ KART IZGARASI & TABLO */}
      {/* ==================================================================== */}
      <div className="hidden lg:flex w-full max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 flex-col gap-12 items-center flex-1">
        
        {/* Başlık ve Aylık / Yıllık Seçici */}
        <div className="text-center flex flex-col gap-3.5 items-center max-w-4xl w-full">
          <h1 className="m-0 font-serif font-semibold text-3xl sm:text-4xl lg:text-[44px] leading-tight text-foreground">
            {t.title}
          </h1>
          <p className="m-0 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-3xl">
            {t.sub}
          </p>

          {/* Sadece Yıllık Seçeneği Rozeti */}
          <div className="inline-flex bg-card border border-border rounded-xl p-1.5 font-extrabold text-sm shadow-xs select-none mt-3">
            <span className="py-2 px-5 rounded-lg bg-primary text-primary-foreground shadow-xs font-bold flex items-center gap-2">
              {t.yearly}
              <span className="text-[11px] font-extrabold bg-amber-500 text-white px-2 py-0.5 rounded-full shadow-xs">
                %55
              </span>
            </span>
          </div>
        </div>

        {/* 3 Plan Kartı */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8 w-full max-w-6xl items-stretch">
          {t.plans.map((p, idx) => {
            const isBasic = idx === 0;
            const isPro = idx === 1;
            const isLife = idx === 2;

            let price = p.yrp;
            let per = t.perYr;
            let note = p.noteYr;

            if (isLife) {
              price = p.once;
              per = t.perOnce;
              note = p.note;
            } else if (isPro) {
              price = p.yrp;
              per = t.perYr;
              note = p.noteYr;
            } else if (isBasic) {
              price = p.yrp;
              per = t.perYr;
              note = p.noteYr;
            }

            const isCardCurrentPlan =
              (idx === 0 && userCurrentPlan === 'basic') ||
              (idx === 1 && userCurrentPlan === 'pro') ||
              (idx === 2 && userCurrentPlan === 'lifetime');

            const isCardLifetimeLocked = userCurrentPlan === 'lifetime' && idx < 2;

            return (
              <div
                key={idx}
                className={`bg-card border rounded-2xl p-6 xl:p-8 2xl:p-9 flex flex-col gap-5 relative shadow-sm transition-all duration-200 ${
                  isPro
                    ? 'border-primary ring-2 ring-primary/20 shadow-md lg:-translate-y-1'
                    : 'border-border hover:border-border/80'
                }`}
              >
                {/* Rozetler */}
                {isCardCurrentPlan ? (
                  <span className="absolute -top-3 left-6 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-extrabold text-[11px] tracking-wide py-1 px-3.5 rounded-full shadow-xs">
                    {isTr ? 'MEVCUT PLANINIZ' : 'CURRENT PLAN'}
                  </span>
                ) : (
                  p.badge && (
                    <span className={`absolute -top-3 left-6 text-white font-extrabold text-[11px] tracking-wide py-1 px-3.5 rounded-full shadow-xs ${
                      isLife ? 'bg-gradient-to-r from-blue-600 to-indigo-600' : 'bg-amber-500'
                    }`}>
                      {p.badge}
                    </span>
                  )
                )}

                <div>
                  <div className="font-extrabold text-[17px] xl:text-[19px] text-foreground">{p.name}</div>
                  <div className="font-normal text-[13.5px] xl:text-[14.5px] leading-snug text-muted-foreground mt-1 min-h-[40px]">
                    {p.tag}
                  </div>
                </div>

                {/* Fiyat Alanı */}
                <div className="flex flex-col gap-1">
                  {p.old && (
                    <span className="font-semibold text-sm xl:text-base text-muted-foreground/60 line-through">
                      {p.old}
                    </span>
                  )}
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-extrabold text-3xl sm:text-4xl xl:text-[42px] text-foreground tracking-tight">
                      {price}
                    </span>
                    {per && <span className="font-semibold text-sm xl:text-base text-muted-foreground">{per}</span>}
                  </div>
                  <span className="text-xs xl:text-sm text-muted-foreground">{note}</span>
                </div>

                {/* Buton */}
                <button
                  onClick={() => handlePlanClick(idx)}
                  disabled={checkoutLoading || isCardCurrentPlan || isCardLifetimeLocked}
                  className={`w-full text-center font-bold text-[14px] xl:text-[15px] py-3.5 px-4 rounded-xl transition-all shadow-xs ${
                    isCardCurrentPlan
                      ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 cursor-not-allowed opacity-90'
                      : isCardLifetimeLocked
                      ? 'bg-muted text-muted-foreground border border-border cursor-not-allowed opacity-75'
                      : isPro
                      ? 'bg-gradient-to-r from-[#2b7fff] to-[#5aa9ff] hover:from-[#2563eb] hover:to-[#3b82f6] text-white shadow-md hover:shadow-lg cursor-pointer'
                      : 'bg-primary text-primary-foreground hover:opacity-90 cursor-pointer'
                  }`}
                >
                  {isCardCurrentPlan
                    ? (isTr ? 'Mevcut Planınız' : 'Current Plan')
                    : isCardLifetimeLocked
                    ? (isTr ? 'Zaten Ömür Boyu Erişiminiz Var' : 'Already Have Lifetime Access')
                    : p.cta}
                </button>

                {/* Özellik Maddeleri */}
                <div className="flex flex-col gap-3 border-t border-border/80 pt-5 mt-auto">
                  {p.feats.map((f, fIdx) => {
                    const isChecked = f[0] === '✓';
                    return (
                      <div
                        key={fIdx}
                        className={`flex gap-3 items-start text-[13.5px] xl:text-[14.5px] leading-snug font-medium ${
                          isChecked ? 'text-foreground' : 'text-muted-foreground/60'
                        }`}
                      >
                        <span
                          className={`font-extrabold flex-none ${
                            isChecked ? 'text-primary' : 'text-muted-foreground/40'
                          }`}
                        >
                          {f[0]}
                        </span>
                        <span>{f[1]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Karşılaştırma Tablosu */}
        <div className="w-full max-w-6xl bg-card border border-border rounded-2xl overflow-hidden shadow-xs">
          <div className="grid grid-cols-4 p-4 sm:p-5 xl:p-6 2xl:p-7 border-b border-border font-extrabold text-xs sm:text-sm tracking-wider text-muted-foreground uppercase bg-muted/30">
            <span className="col-span-1">{t.compare}</span>
            <span className="text-center">{t.pBasic}</span>
            <span className="text-center text-primary font-black">{t.pPro}</span>
            <span className="text-center">{t.pLife}</span>
          </div>

          <div className="divide-y divide-border/60">
            {t.rows.map((r, rIdx) => (
              <div
                key={rIdx}
                className="grid grid-cols-4 p-4 sm:p-5 xl:p-6 text-[14px] sm:text-[15px] xl:text-[16px] font-semibold text-foreground items-center hover:bg-muted/20 transition-colors"
              >
                <span className="col-span-1 text-muted-foreground font-medium">{r[0]}</span>
                <span className="text-center text-muted-foreground">{r[1]}</span>
                <span className="text-center text-primary font-bold">{r[2]}</span>
                <span className="text-center text-foreground font-bold">{r[3]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Güven Rozetleri */}
        <div className="flex flex-wrap gap-8 sm:gap-12 xl:gap-16 items-center justify-center font-semibold text-sm sm:text-base text-muted-foreground py-2">
          <span className="flex items-center gap-2">
            <span className="text-emerald-500 font-bold">✓</span> {t.cancel}
          </span>
          <span className="flex items-center gap-2">
            <span className="text-emerald-500 font-bold">✓</span> {t.vat}
          </span>
          <span className="flex items-center gap-2.5">
            <span>🔒 {t.secure}</span>
            <span className="border border-border bg-card rounded px-2 py-0.5 font-bold text-xs text-foreground">
              VISA
            </span>
            <span className="border border-border bg-card rounded px-2 py-0.5 font-bold text-xs text-foreground">
              MC
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

export const Pricing = PricingView;
export default PricingView;
